import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, Instagram, Facebook, Star } from "lucide-react";
import { RAFFLE } from "@/lib/raffleData";

export default function ContactAndFooter() {
  return (
    <>
      {/* Contact CTA */}
      <section
        id="contacto"
        className="relative py-24 md:py-28 overflow-hidden"
        data-testid="contact-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#051A0F] to-[#0A2818]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#25D366]/15 blur-[120px]" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#25D366]/30 bg-[#25D366]/10 mb-6">
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#25D366] font-semibold font-outfit">
                Habla con nosotros
              </span>
            </div>
            <h2 className="font-bebas text-5xl md:text-7xl text-[#FDFBF7] tracking-wider">
              ¿Listo para <span className="gold-shimmer">ganar</span>?
            </h2>
            <p className="mt-4 text-lg md:text-xl text-[#FDFBF7]/80 max-w-2xl mx-auto font-outfit">
              Escríbenos ahora y aseguramos tu ficha. Cupos limitados por proyecto.
            </p>

            <motion.a
              href="#elegir-ficha"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="mt-10 inline-flex items-center gap-4 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] px-10 py-5 rounded-full font-bebas text-3xl md:text-4xl tracking-wider shadow-[0_20px_60px_-15px_rgba(240,185,27,0.7)]"
              data-testid="contact-whatsapp"
            >
              <MessageCircle className="w-8 h-8" />
              Elegir mi ficha
            </motion.a>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-sm text-[#FDFBF7]/60 font-outfit">
              <span className="inline-flex items-center gap-2">
                <Star className="w-4 h-4 text-[#F0B91B] fill-[#F0B91B]" />
                Atención rápida
              </span>
              <span className="hidden sm:inline">·</span>
              <span>Respuesta en menos de 5 minutos</span>
              <span className="hidden sm:inline">·</span>
              <span>Pagos seguros</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Editorial marquee ribbon */}
      <div className="relative bg-[#F0B91B] py-5 border-y-4 border-[#051A0F]">
        <Marquee speed={55} gradient={false} autoFill>
          <span className="flex items-center gap-8 pr-8 font-bebas text-3xl md:text-5xl text-[#051A0F] tracking-wider">
            <span>★ Inversión Inteligente</span>
            <span className="text-[#051A0F]/40">·</span>
            <span>Futuro Asegurado</span>
            <span className="text-[#051A0F]/40">·</span>
          </span>
        </Marquee>
      </div>

      {/* Footer */}
      <footer className="relative bg-[#030F08] pt-16 pb-8" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pb-10 border-b border-[#F0B91B]/15">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F0B91B] to-[#E5A200] flex items-center justify-center">
                  <span className="font-bebas text-[#051A0F] text-xl">B</span>
                </div>
                <span className="font-bebas text-3xl text-[#F0B91B] tracking-widest">
                  {RAFFLE.brand}
                </span>
              </div>
              <p className="text-sm text-[#FDFBF7]/60 max-w-md font-outfit leading-relaxed">
                Proyectos oficiales autorizados. Cada ficha cuenta, cada sorteo transparente.
                Únete a la comunidad {RAFFLE.brand} y sé el próximo ganador.
              </p>
              <div className="mt-6 flex items-center gap-3">
                <a
                  href="#elegir-ficha"
                  className="w-10 h-10 rounded-full bg-[#F0B91B]/15 hover:bg-[#F0B91B] border border-[#F0B91B]/40 text-[#F0B91B] hover:text-[#051A0F] flex items-center justify-center transition-all"
                  data-testid="footer-whatsapp"
                  aria-label="Elegir ficha"
                >
                  <MessageCircle className="w-4 h-4" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full bg-[#F0B91B]/10 hover:bg-[#F0B91B] border border-[#F0B91B]/40 text-[#F0B91B] hover:text-[#051A0F] flex items-center justify-center transition-all"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full bg-[#F0B91B]/10 hover:bg-[#F0B91B] border border-[#F0B91B]/40 text-[#F0B91B] hover:text-[#051A0F] flex items-center justify-center transition-all"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-bebas text-xl text-[#FDFBF7] tracking-wider mb-4">
                Enlaces
              </h4>
              <ul className="space-y-2 text-sm text-[#FDFBF7]/70 font-outfit">
                <li><a href="#premios" className="hover:text-[#F0B91B] transition-colors">Entregas</a></li>
                <li><a href="#ficha" className="hover:text-[#F0B91B] transition-colors">Ficha</a></li>
                <li><a href="#como" className="hover:text-[#F0B91B] transition-colors">Cómo participar</a></li>
                <li><a href="#contacto" className="hover:text-[#F0B91B] transition-colors">Contacto</a></li>
                <li>
                  <Link to="/login" className="hover:text-[#F0B91B] transition-colors" data-testid="footer-seller-login">
                    Portal vendedores
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bebas text-xl text-[#FDFBF7] tracking-wider mb-4">
                Contacto
              </h4>
              <ul className="space-y-3 text-sm text-[#FDFBF7]/70 font-outfit">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#F0B91B]" />
                  {RAFFLE.whatsappDisplay}
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-[#25D366]" />
                  WhatsApp 24/7
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#FDFBF7]/40 font-outfit">
            <p>© {new Date().getFullYear()} {RAFFLE.brand}. Todos los derechos reservados.</p>
            <p className="uppercase tracking-widest">Juegue responsablemente · Mayores de 18 años</p>
          </div>
        </div>
      </footer>
    </>
  );
}
